// Copyright 2025. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { ISshKeyResponse } from 'common/api/resources/SshKey';
import * as sshKeyActions from 'client/account/actions/sshKeys';
import { CardItem } from 'client/common/components/Card';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import CopyText from 'common/containers/CopyText/CopyText';
import { dataCySelector } from 'common/tests/selectors';
import { TABLE_ACTIONS } from 'client/account/constants/test';
import { ICONS } from 'common/constants';
import { COLORS } from 'client/core/theme';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import {
    Icon,
    Toolbar,
    ToolbarExpander,
    Translate,
} from '@plesk/ui-library';

interface ISshKeyCardProps {
    sshKey: ISshKeyResponse;
}

export type SshKeyCardProps =
    ISshKeyCardProps &
    ReturnType<typeof mapDispatchToProps>;

export const SshKeyCard: React.FC<SshKeyCardProps> = ({
    sshKey,
    sshKeyActions: { removeSshKey },
}) => {
    const handleRemove = () => removeSshKey(sshKey);

    return (
        <CardItem
            isLoading={sshKey.isLoading}
            icon={
                <Icon
                    size={32}
                    name={ICONS.KEY}
                    style={{ color: COLORS.PRIMARY }}
                />
            }
            cardTitle={sshKey.name}
            item={(
                <span>
                    <CopyText width="250px" text={sshKey.body}>
                        {sshKey.body}
                    </CopyText>
                </span>
            )}
            footer={
                <Toolbar>
                    <ToolbarExpander />
                    <ButtonWithConfirmation
                        isLoading={sshKey.isLoading}
                        translations={{
                            text: (
                                <Translate content="account.sshKeys.confirmationRemove.text" />
                            ),
                            button: (
                                <Translate content="account.sshKeys.confirmationRemove.button" />
                            ),
                            tooltip: (
                                <Translate content="account.sshKeys.confirmationRemove.button" />
                            ),
                        }}
                        handleConfirm={handleRemove}
                        data-cy={dataCySelector(1, TABLE_ACTIONS.REMOVE)}
                        icon="recycle"
                    />
                </Toolbar>
            }
        />
    );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    sshKeyActions: bindActionCreators(sshKeyActions, dispatch),
});

export default connect(null, mapDispatchToProps)(SshKeyCard);
